import API from "../api/api_setting/apiConstants";

const MAX_BACKOFF_MS = 10000;
const INITIAL_BACKOFF_MS = 1000;

// Native EventSource — the auth token travels as a query param (the backend's
// authorizeToken/authorizeAdminToken middleware already accepts req.query.token)
// instead of an Authorization header, which EventSource can't set. Unlike a
// hand-rolled fetch()+ReadableStream reader, EventSource streams incrementally
// and reconnects reliably in Safari/WebKit, which is known to buffer fetch()
// response bodies that have no Content-Length (i.e. any long-lived SSE stream).
// Long-lived: reconnects with exponential backoff until unsubscribe() is called.
function createStream(baseUrl, eventName, onEvent) {
  let source = null;
  let reconnectTimer = null;
  let backoff = INITIAL_BACKOFF_MS;
  let stopped = false;

  function connect() {
    if (stopped) return;

    const token = localStorage.getItem(API.AUTH_TOKEN);
    const url = `${baseUrl}?token=${encodeURIComponent(token || "")}`;

    source = new EventSource(url);

    source.addEventListener("connected", () => {
      backoff = INITIAL_BACKOFF_MS;
    });

    source.addEventListener(eventName, (event) => {
      backoff = INITIAL_BACKOFF_MS;
      let parsed;
      try {
        parsed = JSON.parse(event.data);
      } catch (_) {
        return;
      }
      onEvent(parsed);
    });

    source.onerror = () => {
      // A fatal response (e.g. 401 on an expired token) closes the EventSource
      // for good with no built-in retry, so reconnect ourselves in that case.
      // A transient network error leaves it in CONNECTING — the browser is
      // already retrying, so don't stack a second reconnect loop on top of it.
      if (source.readyState === EventSource.CLOSED) {
        scheduleReconnect();
      }
    };
  }

  function scheduleReconnect() {
    if (stopped) return;
    clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(() => {
      backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
      connect();
    }, backoff);
  }

  connect();

  return function unsubscribe() {
    stopped = true;
    clearTimeout(reconnectTimer);
    if (source) source.close();
  };
}

export function subscribeToActivityStream(role, onEvent) {
  const baseUrl = role === 1 ? API.GET_PM_ACTIVITY_STREAM : API.GET_ADMIN_ACTIVITY_STREAM;
  return createStream(baseUrl, "activity", onEvent);
}

// Backend emits notification events under the singular "notification" name
// (see utils/sseManager.js broadcast()), unlike the "activity" stream above.
export function subscribeToNotificationsStream(role, onEvent) {
  const baseUrl = role === 1 ? API.GET_PM_NOTIFICATIONS_STREAM : API.GET_ADMIN_NOTIFICATIONS_STREAM;
  return createStream(baseUrl, "notification", onEvent);
}
